import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrganizerAuth } from '../contexts/OrganizerAuthContext';
import { useEvent } from '../contexts/EventContext';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';

const OrganizerLoginPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { login, loading } = useOrganizerAuth();
  const { event } = useEvent();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const subEventId = event?.subevent_id || event?.id || '';
    const result = await login(email, password, subEventId);
    if (result.success) {
      navigate(slug ? `/event/${slug}/admin` : '/admin');
    } else {
      setError(result.error || 'Invalid credentials or access denied.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center px-4">
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="https://matchmaking.mobivent.fr/logo-2025-Mobivent-400-dark.png"
            alt="Mobivent"
            className="h-12 mx-auto mb-4"
            onError={(e) => e.target.style.display = 'none'}
          />
          <h1 className="text-white text-2xl font-bold">Organizer Access</h1>
          <p className="text-slate-400 text-sm mt-1">
            {event?.name || (slug ? `Event: ${slug}` : 'Matchmaking platform')}
          </p>
        </div>

        {/* Card */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 shadow-2xl">
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full pl-10 pr-10 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-500/20 border border-red-500/40 text-red-300 text-sm px-4 py-3 rounded-xl">
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 text-sm"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Lock size={16} />
              )}
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>

        <p className="text-center text-slate-600 text-xs mt-6">
          Participant access?{' '}
          <a href={slug ? `/event/${slug}/login` : '/login'} className="text-slate-400 hover:text-white transition underline">
            Click here
          </a>
        </p>
      </div>
    </div>
  );
};

export default OrganizerLoginPage;
